import React, { useState, useEffect } from 'react';
import { Sparkles, FileText, Brain, Gavel, CheckCircle } from 'lucide-react';

interface DocumentLoadingScreenProps {
  className?: string;
}

const DocumentLoadingScreen = ({ className = "" }: DocumentLoadingScreenProps) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [dots, setDots] = useState('');

  const loadingMessages = [
    {
      icon: <Brain className="h-6 w-6" />,
      text: "Justiçai analisando sua solicitação",
      color: "text-blue-600"
    },
    {
      icon: <Gavel className="h-6 w-6" />,
      text: "Aplicando expertise jurídica especializada",
      color: "text-purple-600"
    },
    {
      icon: <FileText className="h-6 w-6" />,
      text: "Estruturando seu documento profissional",
      color: "text-green-600"
    },
    {
      icon: <Sparkles className="h-6 w-6" />,
      text: "Finalizando detalhes e formatação",
      color: "text-orange-600"
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      text: "Seu documento está quase pronto",
      color: "text-emerald-600"
    }
  ];

  // Animar pontos
  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(dotsInterval);
  }, []);

  // Alternar mensagens
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 2500);

    return () => clearInterval(messageInterval);
  }, [loadingMessages.length]);

  const currentMessage = loadingMessages[currentMessageIndex];

  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] p-8 ${className}`}>
      {/* Logo Justiçai animada */}
      <div className="mb-8 relative">
        <div className="relative z-10">
          <img 
            src="/logo_justicai_laib.png" 
            alt="Justiçai" 
            className="h-20 w-auto animate-pulse"
          />
        </div>
        {/* Círculo pulsante ao fundo */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-32 w-32 bg-primary/10 rounded-full animate-ping"></div>
        </div>
      </div>

      {/* Título principal */}
      <h2 className="text-3xl font-bold text-foreground mb-2 text-center">
        Criando seu documento
      </h2>
      
      {/* Subtítulo */}
      <p className="text-muted-foreground text-lg mb-8 text-center max-w-md">
        Nossa IA está trabalhando para criar um documento jurídico perfeito para você
      </p>

      {/* Mensagem dinâmica com ícone */}
      <div className="flex items-center gap-3 mb-8 p-4 bg-card border border-border rounded-lg min-w-[400px] justify-center">
        <div className={`${currentMessage.color} animate-pulse`}>
          {currentMessage.icon}
        </div>
        <span className="text-foreground font-medium">
          {currentMessage.text}{dots}
        </span>
      </div>

      {/* Barra de progresso animada */}
      <div className="w-full max-w-md mb-6">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Indicadores de etapas */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        {loadingMessages.map((message, index) => (
          <div
            key={index}
            className={`flex items-center gap-2 transition-all duration-300 ${
              index <= currentMessageIndex 
                ? 'text-primary font-medium' 
                : 'text-muted-foreground'
            }`}
          >
            <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
              index <= currentMessageIndex 
                ? 'bg-primary animate-pulse' 
                : 'bg-muted-foreground/30'
            }`}></div>
            {index < loadingMessages.length - 1 && (
              <div className={`h-0.5 w-8 transition-all duration-300 ${
                index < currentMessageIndex 
                  ? 'bg-primary' 
                  : 'bg-muted-foreground/30'
              }`}></div>
            )}
          </div>
        ))}
      </div>

      {/* Dica útil */}
      <div className="mt-8 p-4 bg-muted/50 border border-border/50 rounded-lg max-w-md text-center">
        <p className="text-sm text-muted-foreground">
          💡 <strong>Dica:</strong> Quanto mais detalhes você fornecer, mais preciso será seu documento
        </p>
      </div>
    </div>
  );
};

export default DocumentLoadingScreen;